import { type CarrierName } from "@inrev/common";

export const INREV_KEY = "inrev";

/**
 * The carriers the legacy bonding-line editor renders and submits rows for. This
 * is an enumeration, not a display order — anything ordering carriers for display
 * should sort on the `priority` the carrier previews endpoint returns.
 */
export const CARRIER_ROW_NAMES: CarrierName[] = [
	"lexington-national-for-accelerant" as CarrierName,
	"accelerant" as CarrierName,
	"coaction" as CarrierName,
];

/**
 * Short labels for compact UI. The carrier table's `displayName` holds formal
 * company names ("New York Marine and General Insurance Company"), which do not
 * fit a legend or a table row.
 */
export const carrierDisplayName: Record<string, string> = {
	[INREV_KEY]: "inRev",
	"lexington-national-for-accelerant": "Lexington National",
	accelerant: "Accelerant",
	coaction: "Coaction",
};

export type CarrierPriorityInput = {
	name: CarrierName;
	priority: number;
};

export const EMPTY_CARRIER_PRIORITIES: CarrierPriorityInput[] = [];

const UNRANKED_CARRIER_PRIORITY = Number.MAX_SAFE_INTEGER;

/**
 * Sorts on the priority the previews endpoint reports. Carriers the API did not
 * rank fall to the end, and because the comparator is stable the caller's order
 * survives untouched until previews load.
 */
export const byCarrierPriority = (carriers: CarrierPriorityInput[]) => {
	const priorityByCarrier = new Map(carriers.map((carrier) => [carrier.name, carrier.priority]));
	const priorityOf = (carrierName: CarrierName) =>
		priorityByCarrier.get(carrierName) ?? UNRANKED_CARRIER_PRIORITY;

	return (a: { carrierName: CarrierName }, b: { carrierName: CarrierName }) =>
		priorityOf(a.carrierName) - priorityOf(b.carrierName);
};
